import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Row, Col, Card, CardBody, Table, Button, Container } from "reactstrap";

import {
  State,
  ChapterSkillsStatsDto,
  LessonSkillStatsDto,
  UserProfileDto,
} from "../../interfaces";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";

import { TableSpinner } from "../../components";
import { NoResultFound } from "../../components/Common";
import { getChaptersSkillsStats } from "../../store/actions";
import { RoleEnum } from "../../enums";

type TProps = {
  subjectId: string;
  onViewSkills: (
    chapter: ChapterSkillsStatsDto,
    lesson: LessonSkillStatsDto
  ) => void;
};
export const SkillsStatisticsPage: React.FC<TProps> = ({
  subjectId,
  onViewSkills,
}) => {
  //#region hooks
  const history = useHistory();
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  //#endregion

  const { loading, response } = useSelector(
    (state: any): State<ChapterSkillsStatsDto[]> =>
      state.chapter.chaptersSkillsStats
  );

  const { response: profile } = useSelector(
    (state: any): State<UserProfileDto> => state.account.userProfile
  );

  useEffect(() => {
    dispatch(getChaptersSkillsStats(subjectId, history));
  }, [subjectId]);

  return (
    <React.Fragment>
      <Container fluid>
        <Row className="mt-2 mb-4 align-items-center">
          <Col className="col-sm-auto">
            <div>
              <h4 className="mb-0">{t("skills")}</h4>
            </div>
          </Col>
          <Col className="col-sm">
            <div className="d-flex justify-content-sm-end gap-2">
              <Button color="success invisible">
                <i className="ri-add-fill me-1 align-bottom"></i>
                Added Me for better Alignment
              </Button>
            </div>
          </Col>
        </Row>

        {loading && <TableSpinner />}
        {!loading && (!response || response.length == 0) && <NoResultFound />}
        {!loading && response && response.length > 0 && (
          <Row>
            {response &&
              response.map((item: ChapterSkillsStatsDto, key) => (
                <Col lg={6} key={key}>
                  <Card>
                    <CardBody>
                      <Row className="g-1 mb-3">
                        <div className="table-responsive">
                          <Table className="table-bordered table-nowrap align-middle mb-0">
                            <thead>
                              <tr style={{ border: "none" }}>
                                <th
                                  colSpan={3}
                                  scope="col"
                                  className="text-left fs-16"
                                  style={{ border: "none" }}
                                >
                                  {i18n.language === "ar"
                                    ? item.name.ar
                                    : item.name.en}
                                </th>
                              </tr>
                            </thead>
                            <thead>
                              <tr>
                                <th scope="col" className="text-center"></th>
                                <th
                                  scope="col"
                                  className="text-center table-success"
                                >
                                  {t("noOfSkills")}
                                </th>
                              </tr>
                            </thead>
                            <thead>
                              <tr>
                                <th scope="col" className="text-center">
                                  {t("totalSkills")} &nbsp;
                                  {i18n.language === "ar" ? (
                                    <i className="ri-arrow-left-line align-bottom"></i>
                                  ) : (
                                    <i className="ri-arrow-right-line align-bottom"></i>
                                  )}
                                </th>
                                <th
                                  scope="col"
                                  className="text-center table-secondary"
                                >
                                  {item.lessons
                                    .map(
                                      (lessonItemTotal: LessonSkillStatsDto) =>
                                        lessonItemTotal.skillsCount
                                    )
                                    .reduce((accumulator, currentValue) => {
                                      return accumulator + currentValue;
                                    }, 0)}
                                </th>
                                <th
                                  scope="col"
                                  className="text-center table-secondary"
                                >
                                  {t("lessons")} &nbsp;
                                  <i className="ri-arrow-down-line align-bottom"></i>
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {item.lessons.map((lessonItem, lessonKey) => (
                                <tr key={lessonKey}>
                                  <td className="fw-medium text-center table-secondary cursor-pointer">
                                    <a
                                      title={t("viewSkills")}
                                      onClick={() => {
                                        onViewSkills(item, lessonItem);
                                      }}
                                    >
                                      {t("viewSkills")}
                                    </a>
                                  </td>
                                  <td
                                    className={`fw-medium text-center ${
                                      profile?.role.key ===
                                        RoleEnum.SUPER_ADMIN &&
                                      lessonItem.isSkillAssociationLesser
                                        ? "bg-danger"
                                        : ""
                                    }`}
                                  >
                                    {lessonItem.skillsCount}
                                  </td>
                                  <td className="fw-medium">
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                      }}
                                    >
                                      <span>
                                        {i18n.language === "ar"
                                          ? lessonItem.name.ar
                                          : lessonItem.name.en}
                                      </span>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
              ))}
          </Row>
        )}
      </Container>
    </React.Fragment>
  );
};
